/**
 * Daily Sales Summary Page
 *
 * Shows today's sales summary for the cashier's outlet.
 * Requires cashier password re-authentication before displaying data.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOutletAuth } from '../../lib/auth/OutletAuthProvider';
import { createPhotoBoothAPI } from '@photobooth/api-client';
import { getEffectiveApiBaseUrl } from '../../lib/config/terminal-urls';
import type { Transaction } from '@photobooth/types';

const apiClient = createPhotoBoothAPI({
  baseUrl: getEffectiveApiBaseUrl(),
});

function getTodayDateRange(): { startDate: string; endDate: string } {
  const d = new Date();
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000 - 1);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

function formatDate(d: Date): string {
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(amount);
}

export function DailySummaryPage() {
  const navigate = useNavigate();
  const { authState, outlet } = useOutletAuth();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const outletId = authState?.outletId;
  const username = authState?.username;

  // Set auth token on API client so transaction requests are authenticated
  useEffect(() => {
    try {
      const cachedToken = localStorage.getItem('photobooth_auth_token');
      if (cachedToken) {
        apiClient.setAuthToken(cachedToken);
      }
    } catch {
      // Non-critical: if storage is unavailable, requests will fail
    }
  }, [authState]);

  useEffect(() => {
    if (!authenticated || !outletId) return;

    const fetchTransactions = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { startDate, endDate } = getTodayDateRange();
        const response = await apiClient.transactions.getByOutlet(outletId, {
          startDate,
          endDate,
          paymentStatus: 'completed',
          limit: 500,
        });

        if (response.success && response.data) {
          const data = Array.isArray(response.data) ? response.data : [];
          setTransactions(data);
        } else {
          setError('Failed to fetch transactions');
        }
      } catch (err) {
        console.error('Error fetching daily summary:', err);
        setError('Failed to fetch transactions');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransactions();
  }, [authenticated, outletId]);

  const handleVerifyPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!outletId || !username || !password.trim()) return;

    setIsVerifying(true);
    setError(null);
    try {
      const response = await apiClient.auth.cashierLogin({
        username,
        password: password.trim(),
        outletId,
      });

      const body = response.data as { success?: boolean; data?: { token?: string } } | undefined;
      if (response.success && body?.data?.token) {
        setAuthenticated(true);
        setPassword('');
      } else {
        setError('Invalid password. Please try again.');
        setPassword('');
      }
    } catch (err) {
      console.error('Password verification error:', err);
      setError('Invalid password. Please try again.');
      setPassword('');
    } finally {
      setIsVerifying(false);
    }
  };

  if (!outletId || !username) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-6 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md text-center">
          <p className="text-gray-600">Unable to load. Please ensure you are logged in as a cashier.</p>
          <button
            type="button"
            onClick={() => navigate('/cashier')}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-6 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Daily Sales Summary</h2>
          <p className="text-gray-600 mb-6">
            Enter your cashier password to view today&apos;s sales data.
          </p>

          <form onSubmit={handleVerifyPassword} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(null);
                }}
                placeholder="Enter your password"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                autoComplete="current-password"
                autoFocus
                disabled={isVerifying}
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate('/cashier')}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isVerifying || !password.trim()}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {isVerifying ? 'Verifying...' : 'View Summary'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  const totalRevenue = transactions.reduce(
    (sum, t) =>
      sum + (t.payment?.amount ?? 0) + (t.addons?.reduce((s, a) => s + a.totalPrice, 0) ?? 0),
    0
  );
  const transactionCount = transactions.length;

  const productBreakdown = transactions.reduce<
    Map<string, { productId: string; name: string; count: number; total: number }>
  >((acc, t) => {
    // Main product
    const mainPid = t.productId;
    const mainName = t.productName ?? t.productId;
    const mainAmount = t.payment?.amount ?? 0;
    if (mainAmount > 0) {
      const existing = acc.get(mainPid);
      if (existing) {
        existing.count += 1;
        existing.total += mainAmount;
      } else {
        acc.set(mainPid, { productId: mainPid, name: mainName, count: 1, total: mainAmount });
      }
    }
    // Add-ons
    for (const addon of t.addons ?? []) {
      const addonPid = addon.productId;
      const addonName = addon.productName ?? 'Add-on';
      const addonAmount = addon.totalPrice;
      const existing = acc.get(addonPid);
      if (existing) {
        existing.count += 1;
        existing.total += addonAmount;
      } else {
        acc.set(addonPid, { productId: addonPid, name: addonName, count: 1, total: addonAmount });
      }
    }
    return acc;
  }, new Map());

  const sortedBreakdown = Array.from(productBreakdown.values()).sort((a, b) => b.total - a.total);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Daily Sales Summary</h1>
          <button
            type="button"
            onClick={() => navigate('/cashier')}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-medium"
          >
            Back to Dashboard
          </button>
        </div>

        <div className="mb-4 text-gray-600">
          {outlet?.name && <span className="font-medium">{outlet.name}</span>}
          <span className="ml-2">— {formatDate(new Date())}</span>
        </div>

        {isLoading ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto" />
            <p className="mt-4 text-gray-600">Loading sales data...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="text-red-600 mb-4">{error}</div>
            <button
              type="button"
              onClick={() => navigate('/cashier')}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Back to Dashboard
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow-md p-6">
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Total Revenue</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{formatCurrency(totalRevenue)}</p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Transactions</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{transactionCount}</p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <h2 className="text-xl font-bold text-gray-900 p-6 border-b border-gray-200">
                Product Breakdown
              </h2>
              {sortedBreakdown.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No completed transactions today.</div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Count
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subtotal
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedBreakdown.map((row) => (
                      <tr key={row.productId}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {row.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                          {row.count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                          {formatCurrency(row.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
